/**
 * Built-in site theme presets.
 *
 * Each preset defines every CSS custom property that drives the visual appearance
 * AND the structural layout of the site. Selecting a preset:
 *  1. Applies all CSS variables to <body> as inline styles (colors, typography, spacing, motion)
 *  2. Dispatches a THEME_LAYOUT_EVENT_NAME event with the layout config (heroVariant,
 *     categoryLayout, productGridColumns) so React components can re-render their structure.
 *
 * Design references:
 *  - Shopify Dawn / Horizon: named color schemes + section layout tokens
 *  - Squarespace: preset palettes + per-variable overrides
 *  - BigCommerce: style variations covering color + layout DNA
 *  - W3C Design Tokens (3-tier: primitive → semantic → component)
 */

// ─── Layout Types ────────────────────────────────────────────────────────────

export type ProductGridColumns = 2 | 3 | 4;
export type HeroVariant = 'full-bleed' | 'split' | 'minimal-text';
export type CategoryLayout = 'sidebar' | 'topbar';

export interface ThemeLayout {
    /** Number of columns in the product grid on desktop (≥ 900px). */
    productGridColumns: ProductGridColumns;
    /** Hero section presentation style on the landing page. */
    heroVariant: HeroVariant;
    /** Category filter presentation on the online store page. */
    categoryLayout: CategoryLayout;
}

export const DEFAULT_LAYOUT: ThemeLayout = {
    productGridColumns: 3,
    heroVariant: 'full-bleed',
    categoryLayout: 'sidebar',
};

/** Custom event name dispatched by applyThemeVars when layout vars change. */
export const THEME_LAYOUT_EVENT_NAME = 'theme-layout-changed';

// ─── CSS Variable Types ───────────────────────────────────────────────────────

export interface ThemeVars {
    // ── Colors ────────────────────────────────────────────────────────────────
    '--landing-cta-background': string;
    '--landing-font-color': string;
    '--navbar-background-color': string;
    '--nav-background-color-rgb': string;
    '--background-color': string;
    '--background-color-hover': string;
    '--background-color-warning': string;
    '--alt-section-bg-1': string;
    '--alt-section-bg-2': string;
    '--surface-color': string;
    '--primary-color': string;
    '--primary-color-rgb': string;
    '--primary-color-hover': string;
    '--secondary-color': string;
    '--secondary-color-hover': string;
    '--selected-background-color': string;
    '--selected-font-color': string;
    '--font-color': string;
    '--font-color-primary': string;
    '--font-color-secondary': string;
    '--font-color-primary-hover': string;
    '--font-color-secondary-hover': string;
    '--font-light': string;
    '--border-color': string;
    '--disabled-bg': string;
    '--disabled-text': string;
    '--card-shadow': string;
    '--modal-shadow': string;
    '--alert-warning-color': string;
    '--alert-warning-bg': string;
    '--success-color': string;
    '--success-hover-color': string;
    '--danger-color': string;
    '--danger-hover-color': string;
    '--form-control-focus-border-color': string;
    '--form-control-focus-shadow': string;
    '--placeholder-color': string;
    '--modal-background-color': string;
    '--modal-border-color': string;
    '--modal-close-button-hover-color': string;
    // Table/row accent colors (reference other vars — not shown in color picker)
    '--highlight-row-color': string;
    '--highlight-row-bg': string;
    '--hover-row-bg': string;

    // ── Typography ────────────────────────────────────────────────────────────
    '--font-family': string;
    '--font-size-base': string;
    '--font-size-small': string;
    '--font-size-large': string;
    '--font-size-xl': string;
    '--modal-header-font-size': string;

    // ── Shape ─────────────────────────────────────────────────────────────────
    '--form-control-border-radius': string;
    '--button-radius': string;
    '--card-radius': string;
    '--modal-border-radius': string;
    '--form-control-padding': string;

    // ── Motion ────────────────────────────────────────────────────────────────
    '--transition-fast': string;
    '--transition-base': string;
    '--form-transition': string;

    // ── Spatial / Layout rhythm ───────────────────────────────────────────────
    '--section-padding': string;
    '--max-content-width': string;
    '--landing-hero-height': string;

    // ── Structural layout (drives React component variant selection) ──────────
    /** Integer: "2" | "3" | "4" — consumed by product-grid CSS + readLayoutFromBody(). */
    '--product-grid-columns': string;
    /** Token: "full-bleed" | "split" | "minimal-text" — consumed by LandingCarousel + readLayoutFromBody(). */
    '--hero-variant': string;
    /** Token: "sidebar" | "topbar" — consumed by OnlineStorePage + readLayoutFromBody(). */
    '--category-layout': string;
}

export interface ThemePreset {
    id: string;
    name: string;
    description: string;
    emoji: string;
    /** Tags shown as small pills on the preset card */
    tags: string[];
    /** Swatch colors for the visual preview card */
    swatch: { primary: string; secondary: string; background: string; accent: string };
    vars: ThemeVars;
    /** Structural layout configuration — drives React component variant selection. */
    layout: ThemeLayout;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const shadow = (r: number, g: number, b: number) =>
    `0 2px 12px rgba(${r}, ${g}, ${b}, 0.10)`;
const focusShadow = (r: number, g: number, b: number) =>
    `0 0 5px rgba(${r}, ${g}, ${b}, 0.45)`;

// ─── Presets ──────────────────────────────────────────────────────────────────

export const THEME_PRESETS: ThemePreset[] = [
    // ─── 1. Forest ───────────────────────────────────────────────────────────
    {
        id: 'forest',
        name: 'Forest',
        description: 'Tonos naturales de bosque. Ideal para productos orgánicos y marcas de bienestar.',
        emoji: '🌿',
        tags: ['Natural', 'Orgánico', 'Suave'],
        swatch: { primary: '#7f9464', secondary: '#8fbf7c', background: '#ffffff', accent: '#e6f5ed' },
        layout: { productGridColumns: 3, heroVariant: 'full-bleed', categoryLayout: 'sidebar' },
        vars: {
            '--landing-cta-background': '#d1d1d1',
            '--landing-font-color': '#ffffff',
            '--navbar-background-color': '#fefefe',
            '--nav-background-color-rgb': '254, 254, 254',
            '--background-color': '#ffffff',
            '--background-color-hover': '#f5f5f5',
            '--background-color-warning': '#f5dfa8',
            '--alt-section-bg-1': '#e6f5ed',
            '--alt-section-bg-2': '#f4f9f4',
            '--surface-color': '#fefefe',
            '--primary-color': '#7f9464',
            '--primary-color-rgb': '127, 148, 100',
            '--primary-color-hover': '#5a7244',
            '--secondary-color': '#8fbf7c',
            '--secondary-color-hover': '#76a466',
            '--selected-background-color': '#d8e8ce',
            '--selected-font-color': '#3d5228',
            '--font-color': '#4d4d4d',
            '--font-color-primary': '#ffffff',
            '--font-color-secondary': '#ffffff',
            '--font-color-primary-hover': '#ffffff',
            '--font-color-secondary-hover': '#ffffff',
            '--font-light': '#bcaaa4',
            '--border-color': '#dcdcdc',
            '--disabled-bg': '#ddd',
            '--disabled-text': '#d1d1d1',
            '--card-shadow': shadow(0, 0, 0),
            '--modal-shadow': '0 4px 14px rgba(0, 0, 0, 0.15)',
            '--alert-warning-color': '#b45309',
            '--alert-warning-bg': '#fff8e1',
            '--success-color': '#4caf50',
            '--success-hover-color': '#388e3c',
            '--danger-color': '#e53935',
            '--danger-hover-color': '#c62828',
            '--form-control-focus-border-color': '#7f9464',
            '--form-control-focus-shadow': focusShadow(127, 148, 100),
            '--placeholder-color': '#aaa',
            '--modal-background-color': '#fdfdfd',
            '--modal-border-color': '#eeeeee',
            '--modal-close-button-hover-color': '#ce9b11',
            '--highlight-row-color': 'var(--font-color)',
            '--highlight-row-bg': 'var(--alert-warning-bg)',
            '--hover-row-bg': 'var(--alt-section-bg-2)',
            // Typography
            '--font-family': '"Poppins", sans-serif',
            '--font-size-base': '1rem',
            '--font-size-small': '0.85rem',
            '--font-size-large': '1.4rem',
            '--font-size-xl': '2.2rem',
            '--modal-header-font-size': '1.7rem',
            // Shape
            '--form-control-border-radius': '8px',
            '--button-radius': '5px',
            '--card-radius': '8px',
            '--modal-border-radius': '10px',
            '--form-control-padding': '14px',
            // Motion
            '--transition-fast': '0.15s ease',
            '--transition-base': '0.25s ease',
            '--form-transition': 'border-color 0.3s ease, box-shadow 0.3s ease',
            // Spatial
            '--section-padding': '60px 20px',
            '--max-content-width': '1100px',
            '--landing-hero-height': '80vh',
            // Layout structure
            '--product-grid-columns': '3',
            '--hero-variant': 'full-bleed',
            '--category-layout': 'sidebar',
        },
    },

    // ─── 2. Midnight ─────────────────────────────────────────────────────────
    {
        id: 'midnight',
        name: 'Midnight',
        description: 'Azul profundo con acentos dorados. Transmite lujo, confianza y sofisticación.',
        emoji: '🌑',
        tags: ['Lujo', 'Premium', 'Elegante'],
        swatch: { primary: '#1e3a5f', secondary: '#c9a227', background: '#f7f9fc', accent: '#edf2f9' },
        layout: { productGridColumns: 2, heroVariant: 'split', categoryLayout: 'sidebar' },
        vars: {
            '--landing-cta-background': 'rgba(30, 58, 95, 0.75)',
            '--landing-font-color': '#ffffff',
            '--navbar-background-color': '#f7f9fc',
            '--nav-background-color-rgb': '247, 249, 252',
            '--background-color': '#f7f9fc',
            '--background-color-hover': '#edf2f9',
            '--background-color-warning': '#fef9e7',
            '--alt-section-bg-1': '#edf2f9',
            '--alt-section-bg-2': '#f3f6fb',
            '--surface-color': '#ffffff',
            '--primary-color': '#1e3a5f',
            '--primary-color-rgb': '30, 58, 95',
            '--primary-color-hover': '#162b47',
            '--secondary-color': '#c9a227',
            '--secondary-color-hover': '#a88320',
            '--selected-background-color': '#d6e4f7',
            '--selected-font-color': '#1e3a5f',
            '--font-color': '#1a2740',
            '--font-color-primary': '#ffffff',
            '--font-color-secondary': '#ffffff',
            '--font-color-primary-hover': '#ffffff',
            '--font-color-secondary-hover': '#1a2740',
            '--font-light': '#8899aa',
            '--border-color': '#d4dde8',
            '--disabled-bg': '#d8dfe8',
            '--disabled-text': '#b0bec8',
            '--card-shadow': shadow(30, 58, 95),
            '--modal-shadow': '0 4px 20px rgba(30, 58, 95, 0.18)',
            '--alert-warning-color': '#92680a',
            '--alert-warning-bg': '#fef9e7',
            '--success-color': '#2e7d32',
            '--success-hover-color': '#1b5e20',
            '--danger-color': '#c62828',
            '--danger-hover-color': '#b71c1c',
            '--form-control-focus-border-color': '#1e3a5f',
            '--form-control-focus-shadow': focusShadow(30, 58, 95),
            '--placeholder-color': '#8899aa',
            '--modal-background-color': '#ffffff',
            '--modal-border-color': '#d4dde8',
            '--modal-close-button-hover-color': '#c9a227',
            '--highlight-row-color': 'var(--font-color)',
            '--highlight-row-bg': 'var(--alert-warning-bg)',
            '--hover-row-bg': 'var(--alt-section-bg-2)',
            // Typography
            '--font-family': '"Poppins", sans-serif',
            '--font-size-base': '1rem',
            '--font-size-small': '0.88rem',
            '--font-size-large': '1.5rem',
            '--font-size-xl': '2.5rem',
            '--modal-header-font-size': '1.9rem',
            // Shape
            '--form-control-border-radius': '6px',
            '--button-radius': '4px',
            '--card-radius': '6px',
            '--modal-border-radius': '8px',
            '--form-control-padding': '14px',
            // Motion
            '--transition-fast': '0.15s ease',
            '--transition-base': '0.3s ease',
            '--form-transition': 'border-color 0.3s ease, box-shadow 0.3s ease',
            // Spatial
            '--section-padding': '80px 24px',
            '--max-content-width': '1200px',
            '--landing-hero-height': '90vh',
            // Layout structure
            '--product-grid-columns': '2',
            '--hero-variant': 'split',
            '--category-layout': 'sidebar',
        },
    },

    // ─── 3. Ocean ────────────────────────────────────────────────────────────
    {
        id: 'ocean',
        name: 'Ocean',
        description: 'Verde azulado con acento coral. Fresco, limpio y energizante.',
        emoji: '🌊',
        tags: ['Fresco', 'Salud', 'Limpio'],
        swatch: { primary: '#0d8a8a', secondary: '#f47567', background: '#ffffff', accent: '#e4f5f5' },
        layout: { productGridColumns: 3, heroVariant: 'full-bleed', categoryLayout: 'topbar' },
        vars: {
            '--landing-cta-background': 'rgba(13, 138, 138, 0.65)',
            '--landing-font-color': '#ffffff',
            '--navbar-background-color': '#ffffff',
            '--nav-background-color-rgb': '255, 255, 255',
            '--background-color': '#ffffff',
            '--background-color-hover': '#e4f5f5',
            '--background-color-warning': '#fff3e0',
            '--alt-section-bg-1': '#e4f5f5',
            '--alt-section-bg-2': '#f0fafa',
            '--surface-color': '#fafefe',
            '--primary-color': '#0d8a8a',
            '--primary-color-rgb': '13, 138, 138',
            '--primary-color-hover': '#097070',
            '--secondary-color': '#f47567',
            '--secondary-color-hover': '#d95e51',
            '--selected-background-color': '#c8eeee',
            '--selected-font-color': '#0a5f5f',
            '--font-color': '#2c3e40',
            '--font-color-primary': '#ffffff',
            '--font-color-secondary': '#ffffff',
            '--font-color-primary-hover': '#ffffff',
            '--font-color-secondary-hover': '#ffffff',
            '--font-light': '#789aa0',
            '--border-color': '#c8e6e6',
            '--disabled-bg': '#d4e8e8',
            '--disabled-text': '#9abfbf',
            '--card-shadow': shadow(13, 138, 138),
            '--modal-shadow': '0 4px 16px rgba(13, 138, 138, 0.15)',
            '--alert-warning-color': '#e65100',
            '--alert-warning-bg': '#fff3e0',
            '--success-color': '#00897b',
            '--success-hover-color': '#00695c',
            '--danger-color': '#f47567',
            '--danger-hover-color': '#c62828',
            '--form-control-focus-border-color': '#0d8a8a',
            '--form-control-focus-shadow': focusShadow(13, 138, 138),
            '--placeholder-color': '#789aa0',
            '--modal-background-color': '#fafefe',
            '--modal-border-color': '#c8e6e6',
            '--modal-close-button-hover-color': '#f47567',
            '--highlight-row-color': 'var(--font-color)',
            '--highlight-row-bg': 'var(--alert-warning-bg)',
            '--hover-row-bg': 'var(--alt-section-bg-2)',
            // Typography
            '--font-family': '"Poppins", sans-serif',
            '--font-size-base': '1rem',
            '--font-size-small': '0.85rem',
            '--font-size-large': '1.4rem',
            '--font-size-xl': '2.2rem',
            '--modal-header-font-size': '1.7rem',
            // Shape
            '--form-control-border-radius': '8px',
            '--button-radius': '8px',
            '--card-radius': '10px',
            '--modal-border-radius': '12px',
            '--form-control-padding': '14px',
            // Motion
            '--transition-fast': '0.12s ease',
            '--transition-base': '0.22s ease',
            '--form-transition': 'border-color 0.25s ease, box-shadow 0.25s ease',
            // Spatial
            '--section-padding': '56px 20px',
            '--max-content-width': '1100px',
            '--landing-hero-height': '75vh',
            // Layout structure
            '--product-grid-columns': '3',
            '--hero-variant': 'full-bleed',
            '--category-layout': 'topbar',
        },
    },

    // ─── 4. Blossom ──────────────────────────────────────────────────────────
    {
        id: 'blossom',
        name: 'Blossom',
        description: 'Rosa profundo y durazno cálido. Elegante y acogedor para boutiques y bienestar.',
        emoji: '🌸',
        tags: ['Elegante', 'Boutique', 'Femenino'],
        swatch: { primary: '#8b3a62', secondary: '#e8926c', background: '#fffbfc', accent: '#fce8ef' },
        layout: { productGridColumns: 2, heroVariant: 'split', categoryLayout: 'topbar' },
        vars: {
            '--landing-cta-background': 'rgba(139, 58, 98, 0.6)',
            '--landing-font-color': '#ffffff',
            '--navbar-background-color': '#fffbfc',
            '--nav-background-color-rgb': '255, 251, 252',
            '--background-color': '#fffbfc',
            '--background-color-hover': '#fce8ef',
            '--background-color-warning': '#fff8e1',
            '--alt-section-bg-1': '#fce8ef',
            '--alt-section-bg-2': '#fdf4f7',
            '--surface-color': '#ffffff',
            '--primary-color': '#8b3a62',
            '--primary-color-rgb': '139, 58, 98',
            '--primary-color-hover': '#6e2e4d',
            '--secondary-color': '#e8926c',
            '--secondary-color-hover': '#c97450',
            '--selected-background-color': '#f5d0e0',
            '--selected-font-color': '#6e2e4d',
            '--font-color': '#3d1f2e',
            '--font-color-primary': '#ffffff',
            '--font-color-secondary': '#ffffff',
            '--font-color-primary-hover': '#ffffff',
            '--font-color-secondary-hover': '#ffffff',
            '--font-light': '#c49aaa',
            '--border-color': '#f0d4de',
            '--disabled-bg': '#e8d4db',
            '--disabled-text': '#c4a0ae',
            '--card-shadow': shadow(139, 58, 98),
            '--modal-shadow': '0 4px 18px rgba(139, 58, 98, 0.14)',
            '--alert-warning-color': '#a16207',
            '--alert-warning-bg': '#fff8e1',
            '--success-color': '#388e3c',
            '--success-hover-color': '#2e7d32',
            '--danger-color': '#d32f2f',
            '--danger-hover-color': '#b71c1c',
            '--form-control-focus-border-color': '#8b3a62',
            '--form-control-focus-shadow': focusShadow(139, 58, 98),
            '--placeholder-color': '#c49aaa',
            '--modal-background-color': '#fffbfc',
            '--modal-border-color': '#f0d4de',
            '--modal-close-button-hover-color': '#e8926c',
            '--highlight-row-color': 'var(--font-color)',
            '--highlight-row-bg': 'var(--alert-warning-bg)',
            '--hover-row-bg': 'var(--alt-section-bg-2)',
            // Typography
            '--font-family': '"Poppins", sans-serif',
            '--font-size-base': '1rem',
            '--font-size-small': '0.86rem',
            '--font-size-large': '1.45rem',
            '--font-size-xl': '2.4rem',
            '--modal-header-font-size': '1.8rem',
            // Shape
            '--form-control-border-radius': '10px',
            '--button-radius': '20px',
            '--card-radius': '14px',
            '--modal-border-radius': '16px',
            '--form-control-padding': '14px',
            // Motion
            '--transition-fast': '0.18s ease',
            '--transition-base': '0.28s ease',
            '--form-transition': 'border-color 0.3s ease, box-shadow 0.3s ease',
            // Spatial
            '--section-padding': '72px 24px',
            '--max-content-width': '1100px',
            '--landing-hero-height': '85vh',
            // Layout structure
            '--product-grid-columns': '2',
            '--hero-variant': 'split',
            '--category-layout': 'topbar',
        },
    },

    // ─── 5. Terra ────────────────────────────────────────────────────────────
    {
        id: 'terra',
        name: 'Terra',
        description: 'Terracota y verde olivo. Artesanal, cálido y cercano. Perfecto para lo local.',
        emoji: '🏺',
        tags: ['Artesanal', 'Local', 'Cálido'],
        swatch: { primary: '#c05621', secondary: '#6e8f4a', background: '#fffbf7', accent: '#fdf0e8' },
        layout: { productGridColumns: 3, heroVariant: 'minimal-text', categoryLayout: 'sidebar' },
        vars: {
            '--landing-cta-background': 'rgba(192, 86, 33, 0.65)',
            '--landing-font-color': '#ffffff',
            '--navbar-background-color': '#fffbf7',
            '--nav-background-color-rgb': '255, 251, 247',
            '--background-color': '#fffbf7',
            '--background-color-hover': '#fdf0e8',
            '--background-color-warning': '#fef9e0',
            '--alt-section-bg-1': '#fdf0e8',
            '--alt-section-bg-2': '#fef7f3',
            '--surface-color': '#ffffff',
            '--primary-color': '#c05621',
            '--primary-color-rgb': '192, 86, 33',
            '--primary-color-hover': '#9c4419',
            '--secondary-color': '#6e8f4a',
            '--secondary-color-hover': '#556f38',
            '--selected-background-color': '#f9d9c4',
            '--selected-font-color': '#7a3010',
            '--font-color': '#3d2010',
            '--font-color-primary': '#ffffff',
            '--font-color-secondary': '#ffffff',
            '--font-color-primary-hover': '#ffffff',
            '--font-color-secondary-hover': '#ffffff',
            '--font-light': '#b8977e',
            '--border-color': '#e8d4c4',
            '--disabled-bg': '#ddd4cc',
            '--disabled-text': '#c0b0a0',
            '--card-shadow': shadow(192, 86, 33),
            '--modal-shadow': '0 4px 18px rgba(192, 86, 33, 0.13)',
            '--alert-warning-color': '#9c6e00',
            '--alert-warning-bg': '#fef9e0',
            '--success-color': '#558b2f',
            '--success-hover-color': '#33691e',
            '--danger-color': '#c62828',
            '--danger-hover-color': '#b71c1c',
            '--form-control-focus-border-color': '#c05621',
            '--form-control-focus-shadow': focusShadow(192, 86, 33),
            '--placeholder-color': '#b8977e',
            '--modal-background-color': '#fffbf7',
            '--modal-border-color': '#e8d4c4',
            '--modal-close-button-hover-color': '#c05621',
            '--highlight-row-color': 'var(--font-color)',
            '--highlight-row-bg': 'var(--alert-warning-bg)',
            '--hover-row-bg': 'var(--alt-section-bg-2)',
            // Typography
            '--font-family': '"Poppins", sans-serif',
            '--font-size-base': '0.95rem',
            '--font-size-small': '0.82rem',
            '--font-size-large': '1.35rem',
            '--font-size-xl': '2rem',
            '--modal-header-font-size': '1.6rem',
            // Shape
            '--form-control-border-radius': '5px',
            '--button-radius': '3px',
            '--card-radius': '5px',
            '--modal-border-radius': '6px',
            '--form-control-padding': '12px',
            // Motion
            '--transition-fast': '0.15s ease',
            '--transition-base': '0.25s ease',
            '--form-transition': 'border-color 0.3s ease, box-shadow 0.3s ease',
            // Spatial
            '--section-padding': '48px 16px',
            '--max-content-width': '1100px',
            '--landing-hero-height': '65vh',
            // Layout structure
            '--product-grid-columns': '3',
            '--hero-variant': 'minimal-text',
            '--category-layout': 'sidebar',
        },
    },

    // ─── 6. Slate ────────────────────────────────────────────────────────────
    {
        id: 'slate',
        name: 'Slate',
        description: 'Azul eléctrico con violeta. Moderno, minimalista y tecnológico.',
        emoji: '💙',
        tags: ['Moderno', 'Minimalista', 'Tech'],
        swatch: { primary: '#2563eb', secondary: '#7c3aed', background: '#fafbff', accent: '#eff4ff' },
        layout: { productGridColumns: 4, heroVariant: 'full-bleed', categoryLayout: 'topbar' },
        vars: {
            '--landing-cta-background': 'rgba(37, 99, 235, 0.6)',
            '--landing-font-color': '#ffffff',
            '--navbar-background-color': '#ffffff',
            '--nav-background-color-rgb': '255, 255, 255',
            '--background-color': '#fafbff',
            '--background-color-hover': '#eff4ff',
            '--background-color-warning': '#fffbeb',
            '--alt-section-bg-1': '#eff4ff',
            '--alt-section-bg-2': '#f5f7ff',
            '--surface-color': '#ffffff',
            '--primary-color': '#2563eb',
            '--primary-color-rgb': '37, 99, 235',
            '--primary-color-hover': '#1d4ed8',
            '--secondary-color': '#7c3aed',
            '--secondary-color-hover': '#6d28d9',
            '--selected-background-color': '#dbeafe',
            '--selected-font-color': '#1e40af',
            '--font-color': '#1e293b',
            '--font-color-primary': '#ffffff',
            '--font-color-secondary': '#ffffff',
            '--font-color-primary-hover': '#ffffff',
            '--font-color-secondary-hover': '#ffffff',
            '--font-light': '#94a3b8',
            '--border-color': '#e2e8f0',
            '--disabled-bg': '#e2e8f0',
            '--disabled-text': '#94a3b8',
            '--card-shadow': shadow(37, 99, 235),
            '--modal-shadow': '0 4px 20px rgba(37, 99, 235, 0.14)',
            '--alert-warning-color': '#d97706',
            '--alert-warning-bg': '#fffbeb',
            '--success-color': '#16a34a',
            '--success-hover-color': '#15803d',
            '--danger-color': '#dc2626',
            '--danger-hover-color': '#b91c1c',
            '--form-control-focus-border-color': '#2563eb',
            '--form-control-focus-shadow': focusShadow(37, 99, 235),
            '--placeholder-color': '#94a3b8',
            '--modal-background-color': '#ffffff',
            '--modal-border-color': '#e2e8f0',
            '--modal-close-button-hover-color': '#7c3aed',
            '--highlight-row-color': 'var(--font-color)',
            '--highlight-row-bg': 'var(--alert-warning-bg)',
            '--hover-row-bg': 'var(--alt-section-bg-2)',
            // Typography
            '--font-family': '"Poppins", sans-serif',
            '--font-size-base': '0.9rem',
            '--font-size-small': '0.8rem',
            '--font-size-large': '1.4rem',
            '--font-size-xl': '2.3rem',
            '--modal-header-font-size': '1.75rem',
            // Shape
            '--form-control-border-radius': '8px',
            '--button-radius': '10px',
            '--card-radius': '12px',
            '--modal-border-radius': '14px',
            '--form-control-padding': '12px',
            // Motion
            '--transition-fast': '0.12s ease',
            '--transition-base': '0.2s ease',
            '--form-transition': 'border-color 0.2s ease, box-shadow 0.2s ease',
            // Spatial
            '--section-padding': '56px 20px',
            '--max-content-width': '1280px',
            '--landing-hero-height': '70vh',
            // Layout structure
            '--product-grid-columns': '4',
            '--hero-variant': 'full-bleed',
            '--category-layout': 'topbar',
        },
    },
];

/** Returns the preset matching the given token, or null if not found. */
export function getPresetById(token: string | null | undefined): ThemePreset | null {
    if (!token) return null;
    return THEME_PRESETS.find((p) => p.id === token) ?? null;
}

// ─── Layout helpers ───────────────────────────────────────────────────────────

const VALID_HERO_VARIANTS: HeroVariant[] = ['full-bleed', 'split', 'minimal-text'];
const VALID_CATEGORY_LAYOUTS: CategoryLayout[] = ['sidebar', 'topbar'];
const VALID_GRID_COLS: ProductGridColumns[] = [2, 3, 4];

function resolveLayoutFromVars(vars: Record<string, string>): ThemeLayout {
    const hero = vars['--hero-variant']?.trim() as HeroVariant;
    const cat = vars['--category-layout']?.trim() as CategoryLayout;
    const cols = parseInt(vars['--product-grid-columns']?.trim() ?? '') as ProductGridColumns;
    return {
        heroVariant: VALID_HERO_VARIANTS.includes(hero) ? hero : DEFAULT_LAYOUT.heroVariant,
        categoryLayout: VALID_CATEGORY_LAYOUTS.includes(cat) ? cat : DEFAULT_LAYOUT.categoryLayout,
        productGridColumns: VALID_GRID_COLS.includes(cols) ? cols : DEFAULT_LAYOUT.productGridColumns,
    };
}

/**
 * Reads the active layout configuration from computed CSS custom properties on <body>.
 * Used at startup by TenantLayoutContext to initialise from the static CSS file values.
 */
export function readLayoutFromBody(): ThemeLayout {
    if (typeof document === 'undefined') return DEFAULT_LAYOUT;
    const style = getComputedStyle(document.body);
    return resolveLayoutFromVars({
        '--hero-variant': style.getPropertyValue('--hero-variant'),
        '--category-layout': style.getPropertyValue('--category-layout'),
        '--product-grid-columns': style.getPropertyValue('--product-grid-columns'),
    });
}

// ─── Theme application ────────────────────────────────────────────────────────

/** Module-level tracker so we can cleanly remove old vars when switching themes. */
const _appliedVarNames = new Set<string>();

/**
 * Applies a merged set of CSS custom properties to <body> as inline styles,
 * overriding the static per-tenant CSS file values with the highest possible specificity.
 * Clears any previously applied vars that are no longer in the new set.
 * Dispatches THEME_LAYOUT_EVENT_NAME if layout vars are present in the new set.
 */
export function applyThemeVars(vars: Record<string, string>): void {
    // Remove stale vars from the previous theme that aren't in the new one
    for (const varName of _appliedVarNames) {
        if (!(varName in vars)) {
            document.body.style.removeProperty(varName);
        }
    }
    _appliedVarNames.clear();

    for (const [name, value] of Object.entries(vars)) {
        document.body.style.setProperty(name, value);
        _appliedVarNames.add(name);
    }

    // Notify layout-aware components of structural changes
    const hasLayoutVars =
        '--hero-variant' in vars ||
        '--category-layout' in vars ||
        '--product-grid-columns' in vars;
    if (hasLayoutVars) {
        window.dispatchEvent(
            new CustomEvent<ThemeLayout>(THEME_LAYOUT_EVENT_NAME, {
                detail: resolveLayoutFromVars(vars),
            }),
        );
    }
}

/**
 * Resolves and applies the active theme to <body>.
 * @param token     - The preset ID stored in TenantSetting.activeThemeToken
 * @param overrides - JSON string of per-variable overrides (TenantSetting.customThemeOverridesJson)
 */
export function resolveAndApplyTheme(
    token: string | null | undefined,
    overrides: string | null | undefined,
): void {
    const preset = getPresetById(token);
    if (!preset) return; // no stored theme → CSS file stays authoritative

    const customOverrides: Record<string, string> = overrides
        ? (() => { try { return JSON.parse(overrides); } catch { return {}; } })()
        : {};

    applyThemeVars({ ...preset.vars, ...customOverrides });
}

// ─── Active-theme detection ───────────────────────────────────────────────────

/** Preset ID used when the current site colors don't match any built-in preset. */
export const CUSTOM_PRESET_ID = '__custom__';

/** Key color vars used to fingerprint which preset is currently active. */
const COLOR_MATCH_VARS: (keyof ThemeVars)[] = [
    '--primary-color',
    '--secondary-color',
    '--background-color',
    '--navbar-background-color',
    '--alt-section-bg-1',
    '--surface-color',
    '--font-color',
];

/**
 * Reads all ThemeVars CSS custom properties from the computed style of <body>.
 * Returns only properties that are present (non-empty).
 */
export function readCurrentThemeVarsFromBody(): Partial<ThemeVars> {
    if (typeof document === 'undefined') return {};
    const style = getComputedStyle(document.body);
    const allVarNames = Object.keys(THEME_PRESETS[0].vars) as (keyof ThemeVars)[];
    const result = {} as Record<string, string>;
    for (const varName of allVarNames) {
        const value = style.getPropertyValue(varName).trim();
        if (value) result[varName] = value;
    }
    return result as Partial<ThemeVars>;
}

/**
 * Tries to find a built-in preset whose key color vars match the given CSS var snapshot.
 * Returns the matching preset ID, or null if no preset matches.
 */
export function detectActivePresetId(currentVars: Partial<ThemeVars>): string | null {
    const cv = currentVars as Record<string, string>;
    for (const preset of THEME_PRESETS) {
        const pv = preset.vars as unknown as Record<string, string>;
        const isMatch = COLOR_MATCH_VARS.every(
            (varName) => (cv[varName] ?? '').toLowerCase() === (pv[varName] ?? '').toLowerCase(),
        );
        if (isMatch) return preset.id;
    }
    return null;
}

/**
 * Builds a virtual "Personalizado" preset from the given CSS var snapshot.
 * Missing vars are filled from the Forest preset as a safe fallback.
 */
export function buildCustomPreset(currentVars: Partial<ThemeVars>): ThemePreset {
    const vars: ThemeVars = { ...THEME_PRESETS[0].vars, ...currentVars } as ThemeVars;
    const layout = resolveLayoutFromVars(vars as unknown as Record<string, string>);
    return {
        id: CUSTOM_PRESET_ID,
        name: 'Personalizado',
        description: 'Configuración actual del sitio. Ajusta colores y estructura a tu gusto.',
        emoji: '🎨',
        tags: ['Actual', 'Personalizado'],
        swatch: {
            primary: vars['--primary-color'] ?? '#888888',
            secondary: vars['--secondary-color'] ?? '#aaaaaa',
            background: vars['--background-color'] ?? '#ffffff',
            accent: vars['--alt-section-bg-1'] ?? '#f5f5f5',
        },
        layout,
        vars,
    };
}
